import { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import type { EditorActions } from '@atlaskit/editor-core';

import { getForgeExtensionProvider } from '@atlassian/xen-editor-provider';
import type { Extension } from '@atlassian/forge-ui';

import type { ForgeUIExtensionDataType } from '@confluence/forge-ui/entry-points/types';
import { getWorkspaceARI } from '@confluence/forge-ui/entry-points/useWorkspaceARI';
import { GQLExtensionContextsFilterType } from '@confluence/forge-ui/entry-points/provider';
import { FlagProvider } from '@confluence/forge-ui/entry-points/flagProvider';
import { getSessionData } from '@confluence/session-data/entry-points/SessionData';
import {
	environmentToForgeProductEnvironment,
	createForgeUIAnalyticsContext,
	createForgeMacroExtensionData,
} from '@confluence/fabric-providers/entry-points/ForgeProvider';
import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { fetchSpaceDetail } from '@confluence/space-utils/entry-points/fetchSpaceDetail';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState';
import { fg } from '@confluence/feature-gating';

import { type EditorAPI } from './extensionHandlerTypes';

type ForgeExtensionProviderParams = Parameters<typeof getForgeExtensionProvider>[0];

export interface GetForgeExtensionHandlerParams {
	spaceKey: string;
	spaceId?: string;
	contentId: string;
	contentType: any;
	mentionProvider: any;
	isEditing: boolean;
	isLivePage: boolean;
	connectExtensionProvider?: ForgeExtensionProviderParams['connectExtensionProvider'];
	editorActions?: EditorActions;
	hideGlassPaneForForge?: boolean;
	locale?: string;
	editorAPI?: EditorAPI;
	useGetContextToken?: (
		extension: Extension,
		extensionData: ForgeUIExtensionDataType,
		localId: string | undefined,
		contextIds: string[],
	) => (contextIdsOverride?: string[]) => Promise<string>;
}

export const getForgeExtensionHandler = async ({
	spaceKey,
	spaceId,
	contentId,
	contentType,
	mentionProvider,
	isEditing,
	connectExtensionProvider,
	editorActions,
	hideGlassPaneForForge,
	locale,
	isLivePage = false,
	editorAPI,
	useGetContextToken,
}: GetForgeExtensionHandlerParams) => {
	const { cloudId, userId, environment, activationId } = await getSessionData();
	const accountId = userId ?? '';

	const apolloClient = getAGGClient();
	const contextIds = [getWorkspaceARI({ cloudId, activationId })];
	const providerFactory = ProviderFactory.create(mentionProvider);

	const getProviderOptions = ({ spaceKey: spaceKeyArg }: { spaceKey: string }) => ({
		accountId,
		cloudId,
		apolloClient,
		contextIds,
		dataProviders: providerFactory,
		environment: environmentToForgeProductEnvironment(environment),
		product: 'confluence',
		page: 'confluence:macroEditor',
		isEditing,
		analyticsWebClient: getAnalyticsWebClient(),
		forgeUIAnalyticsContext: createForgeUIAnalyticsContext({
			objectId: contentId,
			objectType: contentType,
			containerId: spaceKeyArg, // TODO replace with spaceId
			containerType: 'space',
		}),
		extensionData: createForgeMacroExtensionData({
			spaceKey: spaceKeyArg,
			spaceId,
			contentId,
			contentType,
			isLivePage,
		}),
		connectExtensionProvider,
		editorActions,
		FlagProvider,
		extensionsFilter: undefined,
		hideGlassPane: hideGlassPaneForForge,
		locale,
		editorAPI,
		useGetContextToken,
		useRouteActions: fg('confluence_forge_platform_macro_spa_navigation')
			? useRouteActions
			: undefined,
	});

	const forgeExtensionProvider = fetchForgeExtensionProvider({
		getProviderOptions,
		spaceKey,
	});

	return forgeExtensionProvider;
};

export const fetchForgeExtensionProvider = async ({ getProviderOptions, spaceKey }) => {
	return fetchSpaceDetail(spaceKey)
		.then(({ data, loading, error }) => {
			if (!loading) {
				const dataClassificationTags = data?.space?.dataClassificationTags;
				const spaceKeyFromQuery = data?.space?.key;

				return { dataClassificationTags, spaceKeyFromQuery, error };
			}
		})
		.then((data) => {
			if (data?.error) {
				return Promise.reject(data.error);
			}

			const providerOptions = getProviderOptions({
				spaceKey: data?.spaceKeyFromQuery,
			});

			// TODO: Should always use new extensions query regardless of whether there's dataClassificationTags
			// Remove as part of https://product-fabric.atlassian.net/browse/CCECO-3476
			const shouldUseNewExtensionsQuery = !!(
				data?.dataClassificationTags && data?.dataClassificationTags.length > 0
			);

			if (shouldUseNewExtensionsQuery) {
				providerOptions.extensionsFilter = [
					{
						type: GQLExtensionContextsFilterType.ExtensionType,
						value: ['xen:macro'],
					},
				];

				providerOptions.dataClassificationTagIds = data?.dataClassificationTags;
			}

			return getForgeExtensionProvider(providerOptions);
		});
};
